import type { LoyaltyTierBasis } from '@horevia/contracts';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  ErrorState,
  FormField,
  Input,
  NativeSelect,
  NoPermissionState,
  Notice,
  PageHeader,
  SettingSwitch,
  Skeleton,
  Textarea,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Award, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan, useSession } from '@/features/auth/session';
import { crmKeys, loyaltyProgramQueryOptions, upsertLoyaltyProgram } from '@/features/crm/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount } from '@/lib/format';

export const Route = createFileRoute('/_app/crm/loyalty')({ component: LoyaltyPage });

interface TierForm {
  code: string;
  name: string;
  minPoints: string;
  benefits: string;
}

/** Programme de fidélité du tenant (ADR-025, D-H64, R-CRM-08). */
function LoyaltyPage() {
  const { t, i18n } = useTranslation(['crm', 'common', 'errors']);
  const { user } = useSession();
  const canRead = useCan('crm.read');
  const canManage = useCan('crm.manage');
  const queryClient = useQueryClient();
  const query = useQuery(loyaltyProgramQueryOptions(canRead));
  const program = query.data;
  const currency = program?.currencyCode ?? user.tenant.currencyCode;

  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [startsOn, setStartsOn] = useState('');
  const [pointsPerStep, setPointsPerStep] = useState('1');
  const [stepAmount, setStepAmount] = useState('');
  const [pointValue, setPointValue] = useState('0');
  const [earnOnHotel, setEarnOnHotel] = useState(true);
  const [earnOnPos, setEarnOnPos] = useState(true);
  const [includeCompanies, setIncludeCompanies] = useState(false);
  const [tierBasis, setTierBasis] = useState<LoyaltyTierBasis>('ROLLING');
  const [tierWindowMonths, setTierWindowMonths] = useState('12');
  const [tiers, setTiers] = useState<TierForm[]>([]);

  useEffect(() => {
    if (query.data === undefined) return;
    const p = query.data;
    setName(p?.name ?? '');
    setIsActive(p?.isActive ?? false);
    setStartsOn(p?.startsOn ?? '');
    setPointsPerStep(String(p?.pointsPerStep ?? 1));
    setStepAmount(p ? String(p.stepAmount) : '');
    setPointValue(String(p?.pointValue ?? 0));
    setEarnOnHotel(p?.earnOnHotel ?? true);
    setEarnOnPos(p?.earnOnPos ?? true);
    setIncludeCompanies(p?.includeCompanies ?? false);
    setTierBasis(p?.tierBasis ?? 'ROLLING');
    setTierWindowMonths(String(p?.tierWindowMonths ?? 12));
    setTiers(
      (p?.tiers ?? []).map((tier) => ({
        code: tier.code,
        name: tier.name,
        minPoints: String(tier.minPoints),
        benefits: tier.benefits.join('\n'),
      })),
    );
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: () =>
      upsertLoyaltyProgram({
        name: name.trim(),
        isActive,
        startsOn: startsOn || null,
        pointsPerStep: Number(pointsPerStep),
        stepAmount: Number(stepAmount),
        pointValue: Number(pointValue),
        earnOnHotel,
        earnOnPos,
        includeCompanies,
        tierBasis,
        tierWindowMonths: Number(tierWindowMonths),
        tiers: tiers.map((tier) => ({
          code: tier.code.trim(),
          name: tier.name.trim(),
          minPoints: Number(tier.minPoints),
          benefits: tier.benefits
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean),
        })),
        ...(program ? { version: program.version } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: crmKeys.all });
      toast.success(t('crm:loyalty.saved'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const valid =
    name.trim().length > 0 &&
    Number(pointsPerStep) >= 1 &&
    Number(stepAmount) > 0 &&
    Number(pointValue) >= 0 &&
    tiers.every((tier) => tier.code.trim() && tier.name.trim() && Number(tier.minPoints) >= 0);
  const setTier = (index: number, patch: Partial<TierForm>) =>
    setTiers((current) => current.map((tier, i) => (i === index ? { ...tier, ...patch } : tier)));

  if (!canRead) {
    return (
      <Container>
        <PageHeader title={t('crm:loyalty.title')} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionBody')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('crm:loyalty.title')}
        subtitle={t('crm:loyalty.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('crm:title'), link: <Link to="/crm">{t('crm:title')}</Link> },
          { label: t('crm:loyalty.title') },
        ]}
      />
      {query.isError ? (
        <ErrorState
          title={t('errors:generic.title')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:trace.copy')}
          traceCopiedLabel={t('common:trace.copied')}
        />
      ) : query.isPending ? (
        <Skeleton height={320} />
      ) : (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && canManage) mutation.mutate();
          }}
        >
          {!program ? <Notice tone="info">{t('crm:loyalty.noProgramBody')}</Notice> : null}
          {program && !program.isActive ? (
            <Notice tone="warning">{t('crm:loyalty.inactiveNotice')}</Notice>
          ) : null}
          <div className="grid grid-2">
            <Card>
              <CardHeader
                title={t('crm:loyalty.program')}
                icon={<Award className="ico ico-16" aria-hidden="true" />}
                iconTone="amber"
              />
              <CardBody>
                <fieldset
                  disabled={!canManage}
                  className="stack"
                  style={{ border: 0, padding: 0, margin: 0 }}
                >
                  <FormField label={t('crm:loyalty.fields.name')} required>
                    {(field) => (
                      <Input
                        id={field.id}
                        value={name}
                        maxLength={120}
                        onChange={(e) => setName(e.target.value)}
                      />
                    )}
                  </FormField>
                  <SettingSwitch
                    label={t('crm:loyalty.fields.isActive')}
                    description={t('crm:loyalty.fields.isActiveHint')}
                    checked={isActive}
                    onCheckedChange={(value) => setIsActive(Boolean(value))}
                  />
                  <FormField
                    label={t('crm:loyalty.fields.startsOn')}
                    hint={t('crm:loyalty.fields.startsOnHint')}
                  >
                    {(field) => (
                      <Input
                        id={field.id}
                        type="date"
                        value={startsOn}
                        onChange={(e) => setStartsOn(e.target.value)}
                      />
                    )}
                  </FormField>
                  <div className="grid grid-3">
                    <FormField label={t('crm:loyalty.fields.pointsPerStep')} required>
                      {(field) => (
                        <Input
                          id={field.id}
                          inputMode="numeric"
                          value={pointsPerStep}
                          onChange={(e) => setPointsPerStep(e.target.value)}
                        />
                      )}
                    </FormField>
                    <FormField label={t('crm:loyalty.fields.stepAmount')} required>
                      {(field) => (
                        <Input
                          id={field.id}
                          inputMode="decimal"
                          suffix={currency}
                          value={stepAmount}
                          onChange={(e) => setStepAmount(e.target.value)}
                        />
                      )}
                    </FormField>
                    <FormField
                      label={t('crm:loyalty.fields.pointValue')}
                      hint={t('crm:loyalty.fields.pointValueHint')}
                    >
                      {(field) => (
                        <Input
                          id={field.id}
                          inputMode="decimal"
                          suffix={currency}
                          value={pointValue}
                          onChange={(e) => setPointValue(e.target.value)}
                        />
                      )}
                    </FormField>
                  </div>
                  <p className="muted t-12 mb-0">
                    {t('crm:loyalty.fields.summary', {
                      points: pointsPerStep || '0',
                      step: formatAmount(Number(stepAmount) || 0, i18n.language, currency),
                      value: formatAmount(Number(pointValue) || 0, i18n.language, currency),
                    })}
                  </p>
                  <SettingSwitch
                    label={t('crm:loyalty.fields.earnOnHotel')}
                    checked={earnOnHotel}
                    onCheckedChange={(v) => setEarnOnHotel(Boolean(v))}
                  />
                  <SettingSwitch
                    label={t('crm:loyalty.fields.earnOnPos')}
                    checked={earnOnPos}
                    onCheckedChange={(v) => setEarnOnPos(Boolean(v))}
                  />
                  <SettingSwitch
                    label={t('crm:loyalty.fields.includeCompanies')}
                    checked={includeCompanies}
                    onCheckedChange={(v) => setIncludeCompanies(Boolean(v))}
                  />
                  <div className="grid grid-2">
                    <FormField label={t('crm:loyalty.fields.tierBasis')}>
                      {(field) => (
                        <NativeSelect
                          id={field.id}
                          value={tierBasis}
                          onChange={(e) => setTierBasis(e.target.value as LoyaltyTierBasis)}
                        >
                          <option value="ROLLING">{t('crm:loyalty.basis.ROLLING')}</option>
                          <option value="LIFETIME">{t('crm:loyalty.basis.LIFETIME')}</option>
                        </NativeSelect>
                      )}
                    </FormField>
                    <FormField label={t('crm:loyalty.fields.tierWindowMonths')}>
                      {(field) => (
                        <Input
                          id={field.id}
                          inputMode="numeric"
                          value={tierWindowMonths}
                          disabled={tierBasis !== 'ROLLING'}
                          onChange={(e) => setTierWindowMonths(e.target.value)}
                        />
                      )}
                    </FormField>
                  </div>
                </fieldset>
              </CardBody>
            </Card>
            <Card>
              <CardHeader
                title={t('crm:loyalty.fields.tiers')}
                actions={
                  canManage ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      type="button"
                      onClick={() =>
                        setTiers((current) => [
                          ...current,
                          { code: '', name: '', minPoints: '0', benefits: '' },
                        ])
                      }
                    >
                      <Plus className="ico ico-14" aria-hidden="true" />
                      {t('crm:loyalty.fields.addTier')}
                    </Button>
                  ) : undefined
                }
              />
              <CardBody>
                <fieldset
                  disabled={!canManage}
                  className="stack"
                  style={{ border: 0, padding: 0, margin: 0 }}
                >
                  {tiers.length === 0 ? (
                    <p className="muted t-13 mb-0">{t('crm:loyalty.noTier')}</p>
                  ) : null}
                  {tiers.map((tier, index) => (
                    <div key={index} className="stack stack--sm" data-testid={`tier-${index}`}>
                      <div className="grid grid-3">
                        <FormField label={t('crm:loyalty.fields.tierCode')} required>
                          {(field) => (
                            <Input
                              id={field.id}
                              value={tier.code}
                              maxLength={30}
                              onChange={(e) => setTier(index, { code: e.target.value })}
                            />
                          )}
                        </FormField>
                        <FormField label={t('crm:loyalty.fields.tierName')} required>
                          {(field) => (
                            <Input
                              id={field.id}
                              value={tier.name}
                              maxLength={80}
                              onChange={(e) => setTier(index, { name: e.target.value })}
                            />
                          )}
                        </FormField>
                        <FormField label={t('crm:loyalty.fields.tierMinPoints')} required>
                          {(field) => (
                            <Input
                              id={field.id}
                              inputMode="numeric"
                              value={tier.minPoints}
                              onChange={(e) => setTier(index, { minPoints: e.target.value })}
                            />
                          )}
                        </FormField>
                      </div>
                      <FormField label={t('crm:loyalty.fields.tierBenefits')}>
                        {(field) => (
                          <Textarea
                            id={field.id}
                            rows={2}
                            value={tier.benefits}
                            onChange={(e) => setTier(index, { benefits: e.target.value })}
                          />
                        )}
                      </FormField>
                      {canManage ? (
                        <div>
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            onClick={() =>
                              setTiers((current) => current.filter((_, i) => i !== index))
                            }
                          >
                            <Trash2 className="ico ico-14" aria-hidden="true" />
                            {t('crm:loyalty.fields.removeTier')}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </fieldset>
              </CardBody>
            </Card>
          </div>
          {canManage ? (
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <Button
                variant="primary"
                type="submit"
                disabled={!valid || mutation.isPending}
                loading={mutation.isPending}
              >
                {t('crm:loyalty.save')}
              </Button>
            </div>
          ) : null}
        </form>
      )}
    </Container>
  );
}
